import styles from './ScaledMarkerLayer.module.css';
import type { Vendor, Position } from '../../../types';
import { isAmenityType, AMENITY_LABELS } from '../../../utils/amenities';
import { MAP_CONFIG } from '../mapConfig';

export const clampScale = (zoom: number): number =>
  Math.max(0.3, Math.min(1.5, Math.pow(2, zoom - 0.5)));

/** Icon square inside a marker — keep in sync with `.markerIcon` in ScaledMarkerLayer.module.css. */
export const MARKER_ICON_PX = 40;

/** Platform-minimum effective tap target (Apple HIG / Material), in CSS px. */
export const MARKER_HIT_TARGET_PX = 44;

// Below this zoom, marker screen spacing keeps halving per zoom step while a
// constant 44px effective target would stay put — hit areas would heavily
// overlap neighbouring markers and steal their taps. Shrink the effective
// target proportionally below it instead. Matches defaultZoomMobile so the
// platform minimum is guaranteed at the zoom users actually land on.
const HIT_TARGET_FULL_ZOOM = MAP_CONFIG.defaultZoomMobile;

/**
 * Pre-transform px size of a marker's invisible hit area (issue #216).
 * The marker element is scaled by `clampScale(zoom)`, which shrinks the
 * real hit area along with the visuals — so the desired effective
 * (post-transform) target is divided back by that scale. Never below the
 * icon itself. Applied via the `--marker-hit-size` custom property in
 * `updateMarkerPositions` (one write on the markers container).
 */
export function hitAreaSizeFor(zoom: number): number {
  const effectiveTarget =
    MARKER_HIT_TARGET_PX * Math.min(1, Math.pow(2, zoom - HIT_TARGET_FULL_ZOOM));
  return Math.max(MARKER_ICON_PX, effectiveTarget / clampScale(zoom));
}

/** Labels are visible at this zoom level and above. */
export const LABEL_MIN_ZOOM = MAP_CONFIG.minZoom;

// Minimum pointer travel before a pointermove in edit mode is treated as a drag
// (and the position update fires). Intentionally smaller than TAP_MAX_MOVE_PX (12)
// in gestures.ts — that constant governs tap-end tolerance for the tap recognizer,
// while this one governs drag-start detection for the marker drag handler.
export const DRAG_THRESHOLD_PX = 4;

/** Short label text for a vendor marker. */
export function markerLabelFor(vendor: Vendor): string {
  return isAmenityType(vendor.type) ? AMENITY_LABELS[vendor.type] : vendor.name;
}

/** Accessible name for a focusable vendor marker: name plus its location tag. */
export function markerAriaLabel(vendor: Vendor, locationTag: string | undefined): string {
  const loc = locationTag?.trim();
  return loc ? `${vendor.name}, ${loc}` : vendor.name;
}

/**
 * Apply keyboard-accessibility attributes to a vendor marker. Real vendors
 * become focusable buttons (role/tabindex/aria-label) so keyboard users can
 * reach and activate them; amenity markers stay inert (aria-hidden, no tab
 * stop) — they're decorative pins reachable via other surfaces.
 */
export function applyMarkerA11y(el: HTMLElement, vendor: Vendor, locationTag: string | undefined): void {
  if (isAmenityType(vendor.type)) {
    el.setAttribute('aria-hidden', 'true');
    el.removeAttribute('role');
    el.removeAttribute('tabindex');
    el.removeAttribute('aria-label');
    return;
  }
  el.removeAttribute('aria-hidden');
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.setAttribute('aria-label', markerAriaLabel(vendor, locationTag));
}

export function getMarkerClassName(labelPosition: string, editMode: boolean): string {
  const classes = [styles.scaledMarker!];
  if (labelPosition === 'left') classes.push(styles.labelLeft!);
  else if (labelPosition === 'top') classes.push(styles.labelTop!);
  else if (labelPosition === 'bottom') classes.push(styles.labelBottom!);
  else classes.push(styles.labelRight!);
  if (editMode) classes.push(styles.editorMode!);
  return classes.join(' ');
}

export function placeholderLabelClass(lp: string): string {
  if (lp === 'left') return styles.labelLeft!;
  if (lp === 'top') return styles.labelTop!;
  if (lp === 'bottom') return styles.labelBottom!;
  return styles.labelRight!;
}

/** Refresh the className on a placeholder element (labelPosition may change). */
export function setPlaceholderMarkerClass(el: HTMLElement, labelPosition: string): void {
  el.className = [
    styles.scaledMarker!,
    styles.placeholderMarker!,
    placeholderLabelClass(labelPosition),
  ].join(' ');
}

/** Build a fresh vendor marker DOM element. Returns the element and its cached label node. */
export function createVendorMarkerEl(
  vendor: Vendor,
  isEditing: boolean,
  svg: string,
  locationTag: string | undefined,
): { el: HTMLElement; labelEl: HTMLElement } {
  const el = document.createElement('div');
  el.className = getMarkerClassName(vendor.labelPosition, isEditing);
  el.dataset.vendorUid = vendor.uid;
  el.dataset.positionUid = vendor.positionUid;
  el.style.touchAction = isEditing ? 'none' : '';
  applyMarkerA11y(el, vendor, locationTag);

  // Invisible tap-target extender — events bubble to `el`, where the
  // tap/drag handlers live. Sized via --marker-hit-size (see hitAreaSizeFor).
  const hitEl = document.createElement('div');
  hitEl.className = styles.markerHitArea!;
  el.appendChild(hitEl);

  const content = document.createElement('div');
  content.className = styles.markerContent!;
  const iconEl = document.createElement('div');
  iconEl.className = styles.markerIcon!;
  iconEl.innerHTML = svg;
  const labelEl = document.createElement('div');
  labelEl.className = styles.markerLabel!;
  labelEl.textContent = markerLabelFor(vendor);
  const badgeEl = document.createElement('div');
  badgeEl.className = styles.idBadge!;
  badgeEl.textContent = vendor.positionId;
  content.appendChild(iconEl);
  content.appendChild(labelEl);
  content.appendChild(badgeEl);
  el.appendChild(content);
  return { el, labelEl };
}

/** Patch an existing vendor marker element in-place (no rebuild). */
export function updateVendorMarkerEl(
  el: HTMLElement,
  vendor: Vendor,
  labelEl: HTMLElement | undefined,
  isEditing: boolean,
  svg: string,
  locationTag: string | undefined,
): void {
  const iconEl = el.querySelector(`.${styles.markerIcon!}`) as HTMLElement | null;
  if (iconEl) iconEl.innerHTML = svg;
  if (labelEl) labelEl.textContent = markerLabelFor(vendor);
  const badgeEl = el.querySelector(`.${styles.idBadge!}`) as HTMLElement | null;
  if (badgeEl) badgeEl.textContent = vendor.positionId;
  el.className = getMarkerClassName(vendor.labelPosition, isEditing);
  el.style.touchAction = isEditing ? 'none' : '';
  applyMarkerA11y(el, vendor, locationTag);
}

/** Build a placeholder marker for an unassigned position (edit mode only). */
export function createPlaceholderMarkerEl(pos: Position): {
  el: HTMLElement;
  labelEl: HTMLElement;
} {
  const el = document.createElement('div');
  setPlaceholderMarkerClass(el, pos.labelPosition);
  el.style.cursor = 'move';
  el.style.touchAction = 'none';
  // Same invisible tap/drag-target extender as vendor markers.
  const hitEl = document.createElement('div');
  hitEl.className = styles.markerHitArea!;
  el.appendChild(hitEl);
  const content = document.createElement('div');
  content.className = styles.markerContent!;
  const iconEl = document.createElement('div');
  iconEl.className = styles.placeholderIcon!;
  const labelEl = document.createElement('div');
  labelEl.className = styles.markerLabel!;
  content.appendChild(iconEl);
  content.appendChild(labelEl);
  el.appendChild(content);
  return { el, labelEl };
}

/** Build the transient preview marker shown while the Add Position form is open. */
export function createPreviewMarkerEl(): HTMLElement {
  const el = document.createElement('div');
  el.className = `${styles.scaledMarker!} ${styles.placeholderMarker!} ${styles.labelRight!} ${styles.previewMarker!}`;
  const content = document.createElement('div');
  content.className = styles.markerContent!;
  const iconEl = document.createElement('div');
  iconEl.className = styles.placeholderIcon!;
  const labelEl = document.createElement('div');
  labelEl.className = styles.markerLabel!;
  labelEl.textContent = 'new';
  content.appendChild(iconEl);
  content.appendChild(labelEl);
  el.appendChild(content);
  return el;
}
